import type {Component, Editor, StyleProps} from 'grapesjs';
import {EMAIL_MAKER_COMPONENT_TYPES, EMAIL_MAKER_PROPS, STYLE_UPDATE_EVENT_PREFIX} from '@EmailMakerReact/email-maker-react.constants';
import type {StyleAttributes} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import {getAllComponentsInTree, getPrimaryTableComponent, isOfTypeCell, isOfTypeTable} from '@EmailMakerReact/lib/email-maker-component-query-library';
import {updateAllSectionResponsive} from '@EmailMakerReact/lib/email-maker-media-query-library';
import {applyGlobalCellStyles} from '@EmailMakerReact/lib/email-maker-table-style-library';

/** Table/section/divider structure operations for the React email maker. */

const TRANSPARENT_COLOR = 'transparent';
const DEFAULT_TBODY_BACKGROUND_COLOR = 'transparent';

export interface SectionColumnsPreset {
  property: string;
  id: number;
  compValues: number[];
  classUnit: string;
}

export interface DividerCompatibilityOptions {
  component?: Component;
  property: string;
  value: string;
}

/** Mirrors a divider's style change onto its inner divider cell. */
export function updateDividerCompatibilityProperty(editor: Editor, options: DividerCompatibilityOptions): void {
  const component = options.component ?? editor.getSelected();
  if (component?.get('type') !== EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_DIVIDER) {
    return;
  }

  const dividerCell = component.findType(EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_DIVIDER_CELL);
  if (dividerCell.length === 0) {
    return;
  }

  const style: StyleAttributes = {};
  style[options.property] = options.value;
  dividerCell[0].addStyle(style);
  editor.trigger(`${STYLE_UPDATE_EVENT_PREFIX}${options.property}`, dividerCell[0]);
}

/** The selectable section column-width presets. */
export function getSectionColumnsPresets(): SectionColumnsPreset[] {
  return [
    buildSectionColumnsPreset(1, [100]),
    buildSectionColumnsPreset(2, [50, 50]),
    buildSectionColumnsPreset(3, [30, 70]),
    buildSectionColumnsPreset(4, [70, 30]),
    buildSectionColumnsPreset(5, [20, 80]),
    buildSectionColumnsPreset(6, [80, 20]),
    buildSectionColumnsPreset(7, [25, 50, 25]),
    buildSectionColumnsPreset(8, [33, 33, 33]),
    buildSectionColumnsPreset(9, [25, 25, 50]),
    buildSectionColumnsPreset(10, [50, 25, 25]),
    buildSectionColumnsPreset(11, [25, 25, 25, 25]),
  ];
}

/** Adds/removes rows on the selection's primary table to match the count. */
export function updateTableRows(editor: Editor, totalRows: number): void {
  const selected = editor.getSelected();
  if (!selected) {
    return;
  }

  const tableBody = getPrimaryTableComponent(editor, selected);

  if (!tableBody) {
    return;
  }

  updateRowsInTable(editor, tableBody, totalRows);
}

/** Adds/removes columns on every row of the selection's primary table. */
export function updateTableColumns(editor: Editor, totalColumns: number): void {
  const selected = editor.getSelected();
  if (!selected) {
    return;
  }

  const tableBody = getPrimaryTableComponent(editor, selected);

  if (!tableBody) {
    return;
  }

  tableBody.components().each((child) => {
    updateColumnsInRow(editor, child, totalColumns);
  });
}

/** Applies a section column preset (column count + widths), then re-syncs responsiveness. */
export function updateColumnsPreset(editor: Editor, presetID: number, component?: Component): void {
  const presetToApply = getSectionColumnsPresets().find((preset) => preset.id === presetID);
  if (!presetToApply) {
    return;
  }

  const columnWidths = presetToApply.compValues;
  const widthUnit = presetToApply.classUnit;

  const selected = component ?? editor.getSelected();
  if (!selected) {
    return;
  }

  const tableBody = getPrimaryTableComponent(editor, selected);

  if (!tableBody) {
    return;
  }

  tableBody.components().each((row) => {
    updateColumnsInRow(editor, row, columnWidths.length, true);
    updateColumnWidthsFromPreset(row, columnWidths, widthUnit);
  });

  updateAllSectionResponsive(editor, tableBody);
}

/** Mirrors the table's background color onto its tbody (transparent when rounded). */
export function syncTableTbodyColors(component: Component | undefined): void {
  if (!component) {
    return;
  }

  const tableBody = component.find(EMAIL_MAKER_COMPONENT_TYPES.default.TBODY)[0];

  if (!tableBody) {
    return;
  }

  const tableStyles = component.getStyle();
  let colorToApply = DEFAULT_TBODY_BACKGROUND_COLOR;

  if (typeof tableStyles['background-color'] !== 'undefined') {
    colorToApply = tableStyles['background-color'] as string;
  }

  if (doesTableHaveRoundness(component)) {
    colorToApply = TRANSPARENT_COLOR;
  }

  const tbodyStyles: Record<string, string> = {};
  tbodyStyles['background-color'] = colorToApply;

  tableBody.setStyle(tbodyStyles);
}

/**
 * When the table has exactly one tbody child, mirrors the given table styles onto it on every
 * change (background-color flips to transparent while the table is rounded, like syncTableTbodyColors).
 * The model listener is component-scoped, so it dies with the component/editor.
 */
export function addCopyTableStyleToTableBodyListener(component: Component | undefined, stylesToCopy: string[]): void {
  if (!component) {
    return;
  }

  const tableComponents = component.components();
  if (tableComponents.length !== 1) {
    return;
  }

  const listener = stylesToCopy
    .map((style) => {
      return `change:style:${style}`;
    })
    .join(' ');

  component.on(listener, () => {
    const tableBody = tableComponents.at(0);
    const currentStyles = component.getStyle();
    const newStyles: StyleProps = {};

    for (const style of stylesToCopy) {
      let value = currentStyles[style];
      if (style === 'background-color' && doesTableHaveRoundness(component)) {
        value = TRANSPARENT_COLOR;
      }

      if (value) {
        newStyles[style] = value;
      }
    }

    tableBody.setStyle(newStyles);
  });
}

/** Mirrors a table/cell's padding into the mso style prop and the cellpadding attribute. */
export function handleTablePadding(editor: Editor, component: Component | undefined): void {
  if (!component) {
    return;
  }

  if (!isOfTypeTable(editor, component) && !isOfTypeCell(editor, component)) {
    return;
  }

  const styles = component.getStyle();
  const padding = styles.padding;

  if (typeof padding !== 'string' && !Array.isArray(padding)) {
    return;
  }

  // Array padding values join comma-separated (matches CSS multi-value serialization).
  const paddingValue = Array.isArray(padding) ? padding.join(',') : padding;
  component.addStyle({'mso-padding-alt': padding});
  component.addAttributes({
    cellpadding: parseInt(paddingValue, 10),
  });
}

/** Whether the table carries any non-zero border radius. */
export function doesTableHaveRoundness(component: Component | undefined): boolean {
  if (!component) {
    return false;
  }

  const tableStyles = component.getStyle();

  return (
    doesTableHaveRadiusProperty(tableStyles, 'border-radius') ||
    doesTableHaveRadiusProperty(tableStyles, 'border-top-right-radius') ||
    doesTableHaveRadiusProperty(tableStyles, 'border-top-left-radius') ||
    doesTableHaveRadiusProperty(tableStyles, 'border-bottom-right-radius') ||
    doesTableHaveRadiusProperty(tableStyles, 'border-bottom-left-radius')
  );
}

/** Coerces stray default tbodys to PMW_TBODY template-wide (load listener). */
export function validateAllTbodyComponents(editor: Editor): void {
  const wrapper = editor.DomComponents.getWrapper();
  if (!wrapper) {
    return;
  }

  const components = getAllComponentsInTree(wrapper);

  for (const component of components) {
    validateTbodyComponentTypes(component);
  }
}

/** Coerces a default-typed tbody component to PMW_TBODY. */
export function validateTbodyComponentTypes(component: Component | undefined): void {
  if (!component) {
    return;
  }

  if ((component.is(EMAIL_MAKER_COMPONENT_TYPES.default.DEFAULT) || !component.get('type')) && component.get('tagName')?.toLowerCase() === 'tbody') {
    component.set({
      type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_TBODY,
      attributes: {
        ...component.get('attributes'),
        'data-gjs-type': EMAIL_MAKER_COMPONENT_TYPES.table.PMW_TBODY,
        'data-is-pmw-tbody': '1',
        'data-pmw-type': EMAIL_MAKER_COMPONENT_TYPES.table.PMW_TBODY,
      },
    });
  }
}

/** Whether a table/section component is missing its tbody child. */
export function doesTableRequireTbody(component: Component | undefined): boolean {
  if (!component) {
    return false;
  }

  if (!component.is(EMAIL_MAKER_COMPONENT_TYPES.table.PMW_TABLE) && !component.is(EMAIL_MAKER_COMPONENT_TYPES.table.PMW_SECTION)) {
    return false;
  }

  if (component.is(EMAIL_MAKER_COMPONENT_TYPES.table.PMW_TABLE) && component.parent()?.is(EMAIL_MAKER_COMPONENT_TYPES.text.PMW_BUTTON)) {
    return false;
  }

  return component.getChildAt(0)?.get('tagName')?.toLowerCase() !== 'tbody';
}

/** Migrates a legacy divider component to the current divider type, keeping its styles. */
export function updateLegacyDividers(component: Component | undefined): void {
  if (component?.get('type') !== EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_DIVIDER_LEGACY) {
    return;
  }

  const styles = component.getStyle();
  const replacedComponents = component.replaceWith(
    {
      type: EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_DIVIDER,
    },
    {silent: true}
  );

  if (replacedComponents.length === 0) {
    return;
  }

  const newDivider = replacedComponents[0];
  newDivider.setStyle({...newDivider.getStyle(), ...styles}, {silent: true});
}

function buildSectionColumnsPreset(id: number, compValues: number[]): SectionColumnsPreset {
  return {
    property: EMAIL_MAKER_PROPS.COMPONENT_PRESET,
    id,
    compValues,
    classUnit: '%',
  };
}

function updateRowsInTable(editor: Editor, tableComponent: Component, totalRows: number): void {
  let currentRows = tableComponent.components().length;
  const requiredRows = totalRows - currentRows;

  if (requiredRows < 0) {
    const rowComponentsToRemove: Component[] = [];
    tableComponent.components().each((child, i) => {
      if (i < totalRows) {
        return;
      }

      rowComponentsToRemove.push(child);
    });

    rowComponentsToRemove.forEach((row) => {
      row.remove();
    });
    return;
  }

  let currentCells = 0;

  tableComponent.components().each((child) => {
    currentCells = child.components().length;
  });

  const cellsToAdd = [];

  for (let i = 0; i < currentCells; i++) {
    cellsToAdd.push({
      type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_CELL,
    });
  }

  for (currentRows; currentRows < totalRows; currentRows++) {
    const row = tableComponent.components().add(
      {
        type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_ROW,
        components: cellsToAdd,
      },
      {at: currentRows}
    );

    row.components().each((cell) => {
      applyGlobalCellStyles(editor, {cell});
    });
  }
}

function updateColumnsInRow(editor: Editor, rowComponent: Component, totalCells: number, insertEmpty = false): void {
  let currentCells = rowComponent.components().length;
  const requiredCells = totalCells - currentCells;

  if (requiredCells < 0) {
    const cellComponentsToRemove: Component[] = [];
    const cellsToRemove = Math.abs(requiredCells);
    const startIndex = rowComponent.components().length - cellsToRemove;

    rowComponent.components().each((child, i) => {
      if (i >= startIndex) {
        cellComponentsToRemove.push(child);
      }
    });

    cellComponentsToRemove.forEach((cell) => {
      cell.remove();
    });
    return;
  }

  for (currentCells; currentCells < totalCells; currentCells++) {
    const cell = rowComponent.components().add({
      type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_CELL,
    });

    if (insertEmpty) {
      cell.empty();
    }

    applyGlobalCellStyles(editor, {cell, parent: rowComponent});
  }
}

function updateColumnWidthsFromPreset(rowComponent: Component, columnWidths: number[], widthUnit: string): void {
  rowComponent.components().each((cell, i) => {
    const style = cell.getStyle();
    style.width = `${columnWidths[i]}${widthUnit}`;
    cell.setStyle(style);
  });
}

function doesTableHaveRadiusProperty(tableStyles: StyleProps, radiusProperty: string): boolean {
  return typeof tableStyles[radiusProperty] === 'string' && tableStyles[radiusProperty] !== '0px' && tableStyles[radiusProperty] !== '0';
}
